import { getDateDifference, formatDateWithTimezone } from '@travelata-libs/utils';
import { type Leg } from '@/components/Flight';

export const OPERATOR_ID = {
  NTK: 81,
  TezTour: 50,
  AleanTour: 122,
  BiblioGlobus: 11,
  Paks: 92,
  ICS: 26,
  SunMar: 48,
  CoralTravel: 117,
};

export const COUNTRY_ID = {
  Maldives: 56,
};

/**
 * Временный фикс IN-5267: Санмар и Корал считают ночь ночного
 * обратного вылета ночью в отеле, хотя турист выезжает из отеля предыдущим днём.
 */
const OPERATORS_COUNTING_NIGHT_BACK_FLIGHT = new Set([OPERATOR_ID.SunMar, OPERATOR_ID.CoralTravel]);
const NIGHT_BACK_DEPARTURE_END_HOUR = 3;

export const isOperatorTranslateCorrectNightsInHotel = (operatorId: number, countryId: number): boolean => {
  return (
    ![OPERATOR_ID.Paks, OPERATOR_ID.NTK, OPERATOR_ID.ICS].includes(operatorId) &&
    !(operatorId === OPERATOR_ID.BiblioGlobus && countryId === COUNTRY_ID.Maldives)
  );
};

export const isOperatorTranslateCorrectNightsInTour = (operatorId: number, countryId: number): boolean => {
  return (
    ![OPERATOR_ID.Paks, OPERATOR_ID.NTK, OPERATOR_ID.AleanTour, OPERATOR_ID.TezTour].includes(operatorId) &&
    !(operatorId === OPERATOR_ID.BiblioGlobus && countryId === COUNTRY_ID.Maldives)
  );
};

const calculateNightsByDates = ({
  thereDate,
  thereTimezone,
  backDate,
  backTimezone,
}: {
  thereDate: string;
  thereTimezone: string;
  backDate: string;
  backTimezone: string;
}): number => {
  return getDateDifference(
    formatDateWithTimezone(backDate, backTimezone, 'yyyy-MM-dd'),
    formatDateWithTimezone(thereDate, thereTimezone, 'yyyy-MM-dd'),
    'days',
  );
};

export const calculateNightsInTourByFlight = (thereLegs: Leg[], backLegs: Leg[]): number => {
  if (!thereLegs?.length || !backLegs?.length) return 0;

  const thereDate = thereLegs[0].departureDate;
  const thereTimezone = thereLegs[0].airportDeparture?.timezone;
  const backDate = backLegs[0].departureDate;
  const backTimezone = backLegs[0].airportDeparture?.timezone;

  return thereDate && thereTimezone && backDate && backTimezone
    ? calculateNightsByDates({
        thereDate,
        thereTimezone,
        backDate,
        backTimezone,
      })
    : 0;
};

export const calculateNightsInHotelByFlight = (thereLegs: Leg[], backLegs: Leg[]): number => {
  if (!thereLegs?.length || !backLegs?.length) return 0;

  const thereDate = thereLegs.at(-1)?.arrivalDate;
  const thereTimezone = thereLegs.at(-1)?.airportArrival?.timezone;
  const backDate = backLegs[0].departureDate;
  const backTimezone = backLegs[0].airportDeparture?.timezone;

  return thereDate && thereTimezone && backDate && backTimezone
    ? calculateNightsByDates({ thereDate, thereTimezone, backDate, backTimezone })
    : 0;
};

export const isOperatorCountingNightBackFlight = (operatorId: number): boolean =>
  OPERATORS_COUNTING_NIGHT_BACK_FLIGHT.has(operatorId);

export const calculateNightsInHotelByNightBackFlight = (thereLegs: Leg[], backLegs: Leg[]): number => {
  const nightsInHotel = calculateNightsInHotelByFlight(thereLegs, backLegs);
  if (!thereLegs?.length || !backLegs?.length) return nightsInHotel;

  const backDate = backLegs[0].departureDate;
  const backTimezone = backLegs[0].airportDeparture?.timezone;
  if (!backDate || !backTimezone) return nightsInHotel;

  const backDepartureHour = Number(formatDateWithTimezone(backDate, backTimezone, 'H'));
  const isNightBackDeparture = backDepartureHour < NIGHT_BACK_DEPARTURE_END_HOUR;

  return isNightBackDeparture ? Math.max(nightsInHotel - 1, 0) : nightsInHotel;
};
